import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useOrganisation } from '../http-hooks/organisation/useOrganisation';
import { useIntegration } from '../http-hooks/integration/useIntegration';
import { useGlobalStore } from '../zustand-store/useGlobalStore';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { addMixPanelEvent } from '../store/actions';
import { DASHBOARD_TOOL_CLICKED } from '../constants/mixpanel-events';
import { C } from '../styles/tokens';
import DubbingBanner from '../components/DubbingBanner';
import ShortcutTiles from '../components/ShortcutTiles';
import MiniToolsRow from '../components/MiniToolsRow';
import AllToolsGrid from '../components/AllToolsGrid';
import RecentFiles from '../components/RecentFiles';
import BootLoadingScreen from '../components/BootLoadingScreen';

const CARD_PATHS = {
  'AI Voiceover':    'content',
  'Dubbing':         'dubbing',
  'Voice Cloning':   'voice/instant-voice-clone',
  'Music Generator': 'music',
};

function greetingFor(date = new Date()) {
  const h = date.getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Working late';
}

export default function Dashboard() {
  const { org_id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const integrationId = useStoreWithEqualityFn(
    useGlobalStore,
    (s) => s.integrationId,
    shallow
  );

  const { data: org, isLoading } = useOrganisation({ org_id });
  // Integration record carries `content_count` (monotonic count of content ever created for
  // this org+integration). Used to send a brand-new AI Voiceover user straight into the studio.
  const { data: integration } = useIntegration({ org_id, integration_id: integrationId });

  const firstName = org?.owner_name?.split(' ')[0] || '';

  const navigate = (featurePath) => {
    if (!integrationId || !featurePath) return;
    history.push(`/organisation/${org_id}/integration/${integrationId}/${featurePath}`);
  };

  // One event for every tool tile on the dashboard (hero / mini row / all-tools grid),
  // disambiguated by `tool` + `section`. `plan_id` resolves to plan_tier via the Mixpanel
  // lookup table. Curried so each section binds its own `section` value; the tile object
  // carries `tool`, `label`, and (except hero cards) its `nav` path.
  const handleToolClick = (section) => (tile) => {
    dispatch(addMixPanelEvent({
      orgId: org_id, body: {
        eventName: DASHBOARD_TOOL_CLICKED,
        eventData: {
          tool: tile.tool,
          section,
          label: tile.label,
          plan_id: org?.plan_id,
        },
      },
    }));

    // AI Voiceover for a brand-new integration (no content ever created): create a blank
    // content and drop the user straight into the studio, skipping the empty content list.
    // Only when the integration record is loaded and content_count is a firm 0 — an
    // unloaded/undefined count falls through to the normal content list so we never create
    // spurious content for a user who actually has some.
    if (
      tile.tool === 'ai_voiceover' &&
      integrationId &&
      integration &&
      (integration.content_count || 0) === 0
    ) {
      history.push(`/organisation/${org_id}/integration/${integrationId}/content/new`);
      return;
    }

    navigate(tile.nav || CARD_PATHS[tile.label]);
  };

  if (isLoading) {
    return <BootLoadingScreen />;
  }

  return (
    <div style={{ maxWidth: 1320, width: '100%', margin: '0 auto' }}>

      {/* Dubbing announcement strip (dismissible) */}
      <DubbingBanner onTry={() => navigate('dubbing')} />

      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: C.n900, letterSpacing: '-.02em', lineHeight: 1.15, margin: 0 }}>
          {greetingFor()}{firstName ? `, ${firstName}` : ''}.
        </h1>
        <p style={{ fontSize: 15, color: C.n500, marginTop: 6, marginBottom: 0 }}>
          Pick up where you left off, or start something new.
        </p>
      </div>

      {/* Large shortcut tiles */}
      <div style={{ marginBottom: 40 }}>
        <ShortcutTiles onCardClick={handleToolClick('hero_shortcut')} />
      </div>

      {/* Small flat-colored tool row */}
      <MiniToolsRow onToolClick={handleToolClick('mini_tools_row')} />

      {/* All tools grid */}
      <AllToolsGrid onToolClick={handleToolClick('all_tools_grid')} />

      {/* Recent projects */}
      {integrationId && (
        <RecentFiles org_id={org_id} integration_id={integrationId} />
      )}

    </div>
  );
}
